/**
 * vendor-loader.js
 * -----------------------------------------------------------------------------
 * On-demand loader for heavy third-party libraries that used to ship eagerly in
 * the 180KB `libs.min.js` blob (Flickity for carousels, GSAP for animations).
 *
 * That blob was the single largest main-thread cost during page load. Flickity
 * and GSAP are now SEPARATE canonical builds (flickity.pkgd is GSAP-free), and
 * this loader fetches each only when actually needed — so a page with carousels
 * but no animation loads only Flickity (~56KB), never GSAP (~113KB). The loader
 * injects each script once and resolves a Promise when its global is available.
 *
 * Public API (attached to `window.theme`):
 *   theme.loadVendor(key) -> Promise   // key: 'flickity' | 'gsap'
 *   theme.loadFlickity()  -> Promise   // resolves when window.Flickity exists
 *   theme.loadGsap()      -> Promise   // resolves when window.TweenLite exists
 *
 * The script URLs come from `window.theme.vendorUrls`, injected by Liquid in
 * `snippets/dependencies.scripts.liquid`.
 */
(function () {
  'use strict';

  window.theme = window.theme || {};

  // Maps a logical vendor key to (a) the URL to inject and (b) a predicate that
  // tells us the vendor finished initializing (its global is present).
  var REGISTRY = {
    flickity: {
      url: function () { return vendorUrls().flickity; },
      isReady: function () { return typeof window.Flickity !== 'undefined'; }
    },
    gsap: {
      url: function () { return vendorUrls().gsap; },
      isReady: function () { return typeof window.TweenLite !== 'undefined'; }
    }
  };

  // Promise cache so concurrent/repeat calls share one in-flight load.
  var vendorPromises = Object.create(null);
  var scriptPromises = Object.create(null);

  function vendorUrls() {
    return (window.theme && window.theme.vendorUrls) || {};
  }

  // Inject a <script src> exactly once; resolve when it finishes loading.
  // Reuses an existing tag (e.g. injected by another caller) when present.
  function injectScriptOnce(src) {
    if (scriptPromises[src]) {
      return scriptPromises[src];
    }

    scriptPromises[src] = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-vendor-src="' + src + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === '1') {
          resolve();
        } else {
          existing.addEventListener('load', function () { resolve(); }, { once: true });
          existing.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); }, { once: true });
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserve execution order if several are injected
      script.setAttribute('data-vendor-src', src);
      script.addEventListener('load', function () {
        script.setAttribute('data-loaded', '1');
        resolve();
      }, { once: true });
      script.addEventListener('error', function () {
        reject(new Error('Failed to load ' + src));
      }, { once: true });
      document.head.appendChild(script);
    });

    return scriptPromises[src];
  }

  function loadVendor(key) {
    if (vendorPromises[key]) {
      return vendorPromises[key];
    }

    var entry = REGISTRY[key];
    if (!entry) {
      return Promise.reject(new Error('Unknown vendor: ' + key));
    }

    // Already available (e.g. blob still loaded eagerly during rollout phase A).
    if (entry.isReady()) {
      vendorPromises[key] = Promise.resolve();
      return vendorPromises[key];
    }

    var src = entry.url();
    if (!src) {
      return Promise.reject(new Error('No vendor URL configured for: ' + key));
    }

    vendorPromises[key] = injectScriptOnce(src).then(function () {
      if (!entry.isReady()) {
        throw new Error('Vendor "' + key + '" loaded but its global is missing');
      }
    });

    return vendorPromises[key];
  }

  window.theme.loadVendor = loadVendor;
  window.theme.loadFlickity = function () { return loadVendor('flickity'); };
  window.theme.loadGsap = function () { return loadVendor('gsap'); };
})();
